/* eslint-disable unicorn/filename-case */
import { EventAttendeeTypeEnum } from '@app/core/enums/event-attendee-type.enum';
import { LocationModel } from '@app/core/models/location.model';
import {
  EAttendeeInformation,
  EDefaultAttendeeName,
  IBtrEventApiModel,
  IEventRepresentation,
  INewAttendeeInformationApi,
  IPassportBeaconGroupApi,
  IPlaceApiV5Model,
  ITicketApiModel,
  IVerificationTierApi,
} from './api';
import { PlaceModel } from './place.model';
import { TicketModel } from './ticket.model';

export class BtrEventModel {
  id: string;
  slug: string;
  title: string;
  summary: string;
  description: string;
  isPublished: boolean;
  rawStart: string;
  rawEnd: string;
  timezone?: string;
  start: string;
  end: string;
  image: string;
  isDeleted: boolean;
  isExpired?: boolean;
  footnotes?: string;
  contactPhone?: string;
  contactEmail?: string;
  attendees?: Array<IAttendeeInformation>;
  noStartTime: boolean;
  noEndTime: boolean;
  purchaseConfirmationMessage?: string;
  purchaseEmailMessage?: string;
  purchaseSupportEmail?: string;
  onlineEventLink?: string;
  onlineEventDetails?: string;
  representation?: IEventRepresentation;
  showAlone?: boolean;
  showInWhiteLabel?: boolean;
  isAttendeeInfoForEachTicket: boolean;
  attendeeInfo: Array<NewAttendeeInformation>;

  _places: Array<IPlaceApiV5Model> | null;
  _tickets: Array<ITicketApiModel> | null;

  locations: Array<LocationModel> | null;

  formula?: {
    coefs: Array<{
      platformFixed: number;
      platformPersentage: number;
      stripeFixed: number;
      stripePersentage: number;
      threshold: number;
    }>;
    isDefault: boolean;
    isProgressive: boolean;
    name: string;
  };

  isPassport: boolean;
  passportBeaconGroups: Array<PassportBeaconGroup>;
  verificationTiers: Array<VerificationTier>;

  get places(): Array<PlaceModel> {
    return this._places ? this._places.map(item => new PlaceModel(item)) : [];
  }

  get tickets(): Array<TicketModel> {
    return this._tickets
      ? this._tickets.map(item => new TicketModel(item))
      : [];
  }

  constructor(source?: IBtrEventApiModel) {
    if (source == null) {
      return;
    }

    this.id = source.id;
    this.slug = source.slug;
    this.title = source.title;
    this.summary = source.summary;
    this.description = source.description_page?.body?.length
      ? source.description_page?.body
      : source.description;
    this.isPublished = source.is_published;
    this.rawStart = source.start;
    this.rawEnd = source.end;
    this.timezone = source.timezone;
    this.start = source.timezone
      ? new Date(source.start).toLocaleString('en-Us', {
          timeZone: source.timezone,
        })
      : source.start;
    this.end = source.timezone
      ? new Date(source.end).toLocaleString('en-Us', {
          timeZone: source.timezone,
        })
      : source.end;
    this.image = source.image;
    this._places = source.beacons;
    this._tickets = source.tickets?.map(ticket => ({
      ...ticket,
      event: ticket?.event ?? source.id,
    }));
    this.isPassport =
      source.tickets?.length > 0 && source.tickets[0]?.is_passport;
    this.isDeleted = source.is_deleted;
    this.contactEmail = source.contact_email;
    this.contactPhone = source.contact_phone;
    this.footnotes = source.footnotes;
    this.attendees = Object.keys(EventAttendeeTypeEnum)
      .map(key => ({
        type: EventAttendeeTypeEnum[key],
        required:
          source.attendee_info_fields_required?.includes(
            EventAttendeeTypeEnum[key],
          ) || false,

        static: false,
        include:
          source.attendee_info_fields?.includes(EventAttendeeTypeEnum[key]) ||
          source.attendee_info_fields_required?.includes(
            EventAttendeeTypeEnum[key],
          ) ||
          false,
      }))
      .filter(item => item.include || item.required);

    this.noStartTime = source.no_start_time;
    this.noEndTime = source.no_end_time;
    this.purchaseEmailMessage = source.purchase_email_message;
    this.purchaseConfirmationMessage = source.purchase_confirmation_message;
    this.purchaseSupportEmail = source.purchase_support_email;
    this.onlineEventLink = source.online_event_link;
    this.onlineEventDetails = source.online_event_details;
    this.representation = source.representation;
    this.showAlone = source.show_alone ?? false;
    this.showInWhiteLabel = source.show_in_white_label;
    this.isAttendeeInfoForEachTicket =
      source.is_attendee_info_for_each_ticket || false;
    this.locations =
      source.locations?.map(location => new LocationModel(location)) || [];

    this.formula = source?.formula
      ? {
          coefs:
            source.formula.coefs?.map(item => ({
              platformFixed: item.platform_fixed,
              platformPersentage: item.platform_persentage,
              stripeFixed: item.stripe_fixed,
              stripePersentage: item.stripe_persentage,
              threshold: item.threshold,
            })) ?? [],
          isProgressive: source.formula.is_progressive,
          isDefault: source.formula.is_default,
          name: source.formula.name,
        }
      : null;
    this.passportBeaconGroups = mapPassportBeaconGroups(
      source.passport_beacon_groups,
    );
    this.verificationTiers = mapVerificationTiers(source.verification_tiers);
    // this.isPassport = false;
    this.isExpired = source.is_expired;
    this.attendeeInfo =
      source.attendee_info
        ?.filter(info => info.is_visible)
        ?.map(info => new NewAttendeeInformation(info)) ?? [];
  }

  static copyObj(
    source: Pick<BtrEventModel, keyof BtrEventModel>,
  ): BtrEventModel {
    return Object.assign(new BtrEventModel(), source);
  }

  static formAttendeeToServer(
    attendee: NewAttendeeInformation,
    ticketIndex: number,
  ): Array<IAttendeeItemToServer> {
    if (attendee.otherValue?.length) {
      return [
        {
          key: `ticket-${ticketIndex}-${attendee.slug}`,
          value: attendee.value,
        },
        {
          key: `ticket-${ticketIndex}-${
            attendee.slugs[attendee.slugs.length - 1]
          }`,
          value: attendee.otherValue,
        },
      ];
    }
    return [
      {
        key: `ticket-${ticketIndex}-${attendee.slug}`,
        value:
          attendee.type === EAttendeeInformation.PHONE &&
          attendee.value?.length > 0 &&
          !attendee.value?.includes('+')
            ? `+${attendee.value}`
            : attendee.value,
      },
    ];
  }

  static formTicketAttendeeToServer(
    item: TicketModel,
    index: number,
  ): IAttendeeItemToServer {
    return {
      key: `ticket-${index}-ticket`,
      value: item.id,
    };
  }
}

export interface PassportBeaconGroup {
  id: number;
  title: string;
  description?: string;
  threshold: number;
  beaconIds: Array<number>;
}

export interface VerificationTier {
  id: number;
  title: string;
  description?: string;
  howToClaim?: string;
  threshold: number;
  thresholdGroups: number;
  isVerifyTicket: boolean;
}

export function mapPassportBeaconGroups(
  groups?: Array<IPassportBeaconGroupApi> | null,
): Array<PassportBeaconGroup> {
  return (
    groups
      ?.map(group => ({
        id: group.id,
        title: group.title,
        description: group.description,
        threshold: group.threshold,
        beaconIds: group.beacon_ids ?? [],
      }))
      .filter(group => group.beaconIds.length > 0) ?? []
  );
}

export function mapVerificationTiers(
  tiers?: Array<IVerificationTierApi> | null,
): Array<VerificationTier> {
  return (
    tiers?.map(tier => ({
      id: tier.id,
      title: tier.title,
      description: tier.description,
      howToClaim: tier.how_to_claim,
      threshold: tier.threshold,
      thresholdGroups: tier.threshold_groups ?? 0,
      isVerifyTicket: tier.is_verify_ticket ?? false,
    })) ?? []
  );
}

export interface IAttendeeInformation {
  type: EventAttendeeTypeEnum;
  include: boolean;
  required: boolean;
  static: boolean;
  value?: string;
}

export interface IAttendeeItemToServer {
  key: string;
  value: string;
}

export enum EventView {
  INFO = 'info',
  TICKETS = 'tickets',
  ORDER = 'order',
}

export interface IEventGroup {
  title: EventGroupType;
  list: Array<BtrEventModel>;
}

export enum EventGroupType {
  TODAY = 'events-list.today',
  NEXT_WEEK = 'events-list.week',
  NEXT_MONTH = 'events-list.month',
  SEARCH = 'events-list.search',
  LATER = 'events-list.later',
  EXPIRED = 'events-list.expired',
}

export enum EventFilterType {
  NONE = 'none',
  FREE_ONLY = 'free',
  PAID_ONLY = 'paid',
}

export class NewAttendeeInformation {
  slug?: string;
  slugs?: Array<string>;
  name?: string | EDefaultAttendeeName;
  title: string;
  type: EAttendeeInformation;
  choices: Array<{ value: any; text: string }>;
  descriptions?: string;
  isVisible: boolean;
  isRequired: boolean;
  isVisibleInScanner: boolean;
  isRequiredInScanner: boolean;
  value?: any;
  otherValue?: string;

  constructor(source: INewAttendeeInformationApi) {
    this.slug = source?.slug;
    this.slugs = source?.slugs;
    this.name = source?.name;
    this.title = source?.title;
    this.type = source?.type;
    this.choices = source?.choices
      ? Object.keys(source.choices).map(key => ({
          value: key,
          text: source.choices[key],
        }))
      : [];
    this.descriptions = source?.descriptions;
    this.isVisibleInScanner = source?.is_visible_in_scanner;
    this.isVisible = source?.is_visible;
    this.isRequired = source?.is_required;
    this.isRequiredInScanner = source?.is_required_in_scanner;
    this.otherValue = null;
  }
}
